import { createSignal, Show, createEffect, onCleanup, type JSX, type ParentProps } from 'solid-js';

export default function FilterDrawer(
  props: ParentProps<{ activeCount?: () => number }>,
): JSX.Element {
  const [open, setOpen] = createSignal(false);

  createEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = open() ? 'hidden' : '';
  });
  onCleanup(() => {
    if (typeof document !== 'undefined') document.body.style.overflow = '';
  });

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open filters"
        class="lg:hidden fixed bottom-4 right-4 z-30 rounded-full bg-brand-green text-brand-cream px-5 py-3 shadow-lg flex items-center gap-2 text-sm font-semibold"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
          <path
            fill-rule="evenodd"
            d="M2.628 1.601C5.028 1.206 7.49 1 10 1s4.973.206 7.372.601a.75.75 0 0 1 .628.74v2.288a2.25 2.25 0 0 1-.659 1.59l-4.682 4.683a2.25 2.25 0 0 0-.659 1.59v3.037c0 .684-.31 1.33-.844 1.757l-1.937 1.55A.75.75 0 0 1 8 18.25v-5.757a2.25 2.25 0 0 0-.659-1.591L2.659 6.22A2.25 2.25 0 0 1 2 4.63V2.34a.75.75 0 0 1 .628-.74Z"
            clip-rule="evenodd"
          />
        </svg>
        Filters
        <Show when={props.activeCount && (props.activeCount?.() ?? 0) > 0}>
          <span class="ml-1 rounded-full bg-brand-cream text-brand-green text-xs px-2 py-0.5 font-bold">
            {props.activeCount?.()}
          </span>
        </Show>
      </button>

      <Show when={open()}>
        <div
          class="lg:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      </Show>

      <aside
        class={`
          flex flex-col gap-5 bg-white overflow-y-auto
          fixed top-0 bottom-0 left-0 w-80 max-w-[85vw] z-50 shadow-2xl
          transition-transform duration-200 ease-out
          ${open() ? 'translate-x-0' : '-translate-x-full'}
          lg:sticky lg:top-6 lg:bottom-auto lg:left-auto lg:w-auto lg:max-w-none lg:z-auto
          lg:shadow-none lg:rounded-xl lg:border lg:border-brand-hex
          lg:self-start lg:max-h-[calc(100vh-3rem)] lg:translate-x-0
          lg:p-4
        `}
      >
        {/* Mobile header row: dedicated space for the close button so it
            can't collide with a 'Reset all' control inside `children`.
            Hidden on lg where the drawer is a static sidebar. */}
        <div class="lg:hidden sticky top-0 z-10 bg-white border-b border-brand-hex px-4 py-2 flex items-center justify-between">
          <span class="font-display text-sm font-bold text-brand-green">Filters</span>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close filters"
            class="p-2 -mr-2 text-brand-green rounded-full hover:bg-brand-hex/40"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5">
              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
            </svg>
          </button>
        </div>
        <div class="flex flex-col gap-5 p-4 lg:p-0">
          {props.children}
        </div>
      </aside>
    </>
  );
}
